import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, X, Star, Shield, ChevronDown, HelpCircle, ArrowRight,
  Users, BookOpen, Globe2, Award, Sparkles, GraduationCap,
  CalendarDays, Video, FileText, KeyRound,
} from 'lucide-react';
import MembresiasLayout from './MembresiasLayout';
import QualificationFormModal from '@/components/QualificationForm';
import { publicCatalog } from '@/config/membershipCatalog';
import { useSiteLang } from '@/components/site/SiteLang';

const NAV = '#0a1628';
const GOLD = '#c9a84c';
const CREAM = '#faf9f6';
const SERIF = "'Playfair Display', serif";

function GoldCTA({ children, onClick, full, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex max-w-full items-center justify-center gap-2 font-bold text-sm tracking-wider transition-all hover:brightness-[0.85] px-6 sm:px-10 py-4 ${full ? 'w-full' : ''}`}
      style={{ background: GOLD, color: NAV, borderRadius: 4 }}>
      {children}
    </button>
  );
}

const CAT = { es: publicCatalog('es'), en: publicCatalog('en') };

const SHARED_FAQ_ES = [
  { q: '¿Cómo es el proceso para unirme?', a: 'Haces el test de idoneidad (menos de 2 minutos) y eliges día y hora para una llamada con el equipo. En ella te conocemos, resolvemos tus dudas y confirmamos que el club encaja contigo. Después creas tu cuenta y recibes una sesión de bienvenida.' },
  { q: '¿Qué tipo de oportunidades hay dentro del club?', a: 'Oportunidades inmobiliarias reales que el equipo y sus colaboradores trabajan fuera de los portales, presentadas en el área privada con su documentación y su análisis. Cada miembro decide libremente si participa y en cuál, después de formarse: participar es siempre una decisión aparte y voluntaria.' },
  { q: '¿En qué idioma está la formación?', a: 'Todo el contenido está en español. Algunos materiales complementarios están también en inglés.' },
  { q: '¿Cuánto cuesta la membresía?', a: `${CAT.es.priceAnswer} La membresía es anual y se renueva desde tu fecha de alta.` },
  { q: '¿Qué estoy pagando exactamente?', a: CAT.es.feeScopeNote },
  { q: '¿Hay período de satisfacción?', a: CAT.es.satisfactionNote },
];

const CONFIGS_ES = {
  basica: {
    badge: 'MEMBRESÍA PRO — FREEDOM HOMES CLUB',
    title: 'Entiende la nueva economía inmobiliaria desde dentro',
    subtitle: 'Formación avanzada, sesiones especializadas en directo, acceso a oportunidades inmobiliarias reales fuera del mercado y una comunidad privada con la que contrastar criterio. Aprende antes de decidir.',
    ctaText: CAT.es.testCta,
    crossSellText: '¿Quieres certificarte como Broker Digital? Ver Premium',
    crossSellLink: '/membresias/premium',
    forYou: [
      'Quieres acceder a oportunidades inmobiliarias que no llegan a los portales y decidir tú en cuáles participas',
      'Quieres entender a fondo cómo está cambiando el sector inmobiliario',
      'Buscas una comunidad seria de miembros y profesionales',
      'Prefieres formación práctica, con casos reales',
      'Quieres aprender a tu ritmo y con sesiones en directo',
    ],
    notForYou: [
      'Quieres la certificación y el Bootcamp (eso es Premium)',
      'Solo buscas contenido introductorio (eso es Access)',
      'No tienes tiempo para formarte ni asistir a sesiones',
    ],
    benefits: [
      { icon: KeyRound, t: 'Área privada de oportunidades', d: 'Oportunidades inmobiliarias reales fuera del mercado, presentadas con su documentación y su análisis. Tú eliges en qué participas.' },
      { icon: BookOpen, t: 'FHC Academy · formación avanzada', d: 'Programa completo sobre la nueva economía inmobiliaria y la tokenización como disciplina, del fundamento a la práctica.' },
      { icon: Video, t: 'Sesiones especializadas en directo', d: 'Masterclasses y sesiones de preguntas con el equipo y ponentes invitados.' },
      { icon: Users, t: 'Comunidad privada', d: 'Un círculo cerrado de miembros y profesionales con quien compartir criterio.' },
      { icon: Globe2, t: 'Networking entre miembros y profesionales', d: 'Conexiones con miembros y profesionales de distintos países.' },
      { icon: FileText, t: 'Recursos ampliados', d: 'Guías, plantillas y materiales de estudio actualizados.' },
    ],
    timeline: [
      { label: 'Día 1', text: 'Acceso a la plataforma formativa y a la comunidad privada' },
      { label: 'Semana 1', text: 'Sesión de bienvenida y primeros módulos de FHC Academy' },
      { label: 'Mes 1', text: 'Primeras sesiones especializadas en directo y conexiones con otros miembros' },
      { label: 'Mes 3', text: 'Criterio propio para analizar las oportunidades del club y decidir en cuáles participas' },
    ],
    faq: [
      ...SHARED_FAQ_ES.slice(0, 3),
      { q: '¿Puedo pasar a Premium más adelante?', a: 'Sí. Escríbenos y te indicamos las condiciones vigentes para pasar a Premium.' },
      ...SHARED_FAQ_ES.slice(3),
    ],
  },
  premium: {
    badge: 'MEMBRESÍA PREMIUM — FREEDOM HOMES CLUB',
    title: 'Certifícate como Broker Digital en Tokenización Inmobiliaria',
    subtitle: 'Todo lo de PRO, más el Bootcamp Broker Digital presencial, la certificación privada FHC y el acompañamiento grupal semanal en directo.',
    ctaText: CAT.es.testCta,
    crossSellText: '¿Prefieres empezar por la formación? Ver PRO',
    crossSellLink: '/membresias/pro',
    forYou: [
      'Quieres una certificación seria del club en tokenización inmobiliaria',
      'Valoras una inmersión presencial de 4 días con el equipo',
      'Quieres acompañamiento grupal semanal en directo',
      'Quieres saber explicar con rigor la tokenización inmobiliaria en tu entorno profesional',
    ],
    notForYou: [
      'Solo buscas formación y comunidad (eso es PRO)',
      'No puedes asistir a un Bootcamp presencial',
      'Buscas una titulación académica oficial',
    ],
    benefitsIntro: 'TODO LO DE PRO',
    benefits: [
      { icon: CalendarDays, t: 'Bootcamp Broker Digital · 4 días presenciales', d: `Inmersión presencial con el equipo de FHC. ${CAT.es.bootcampNote}` },
      { icon: Award, t: 'Certificación privada FHC como Broker Digital', d: CAT.es.certNote },
      { icon: GraduationCap, t: 'Credencial de Broker Digital', d: 'Una certificación privada que acredita tu formación en el programa, con el criterio y los materiales del equipo.' },
      { icon: Sparkles, t: 'Acompañamiento grupal semanal', d: 'Sesiones semanales en directo para resolver casos y mantenerte al día.' },
      { icon: FileText, t: 'Materiales profesionales del club', d: 'Presentaciones y guías para explicar la tokenización inmobiliaria con rigor.' },
    ],
    timeline: [
      { label: 'Día 1', text: 'Acceso completo a la plataforma, la comunidad y FHC Academy' },
      { label: 'Semana 1', text: 'Sesión de bienvenida y reserva de tu plaza en el próximo Bootcamp' },
      { label: 'Bootcamp', text: '4 días de inmersión presencial y certificación privada FHC' },
      { label: 'Después', text: 'Acompañamiento grupal semanal y materiales profesionales del club' },
    ],
    faq: [
      ...SHARED_FAQ_ES.slice(0, 1),
      { q: '¿Qué incluye el Bootcamp?', a: `Cuatro días presenciales de formación con el equipo, que terminan con la certificación privada FHC. ${CAT.es.bootcampNote} Consulta las próximas ediciones en la sección de eventos.` },
      { q: '¿La certificación es oficial?', a: CAT.es.certNote },
      ...SHARED_FAQ_ES.slice(1),
    ],
  },
};

const SHARED_FAQ_EN = [
  { q: 'How do I join?', a: 'You take the suitability test (under 2 minutes) and choose a day and time for a call with the team. On the call we get to know you, answer your questions and confirm that the club is right for you. You then create your account and receive a welcome session.' },
  { q: 'What kind of opportunities are there inside the club?', a: 'Real property opportunities that the team and its partners source away from the property portals, presented in the members’ area with full documentation and analysis. Each member decides freely whether to take part, and in which ones, after completing the training: taking part is always a separate, voluntary decision.' },
  { q: 'What language is the training in?', a: 'All content is in Spanish. Some supplementary materials are also available in English.' },
  { q: 'How much does membership cost?', a: `${CAT.en.priceAnswer} Membership is annual and renews from the date you join.` },
  { q: 'What exactly am I paying for?', a: CAT.en.feeScopeNote },
  { q: 'Is there a satisfaction period?', a: CAT.en.satisfactionNote },
];

const CONFIGS_EN = {
  basica: {
    badge: 'PRO MEMBERSHIP — FREEDOM HOMES CLUB',
    title: 'Understand the new property economy from the inside',
    subtitle: 'Advanced training, live specialist sessions, access to real off-market property opportunities and a private community to sharpen your judgement. Learn first, then decide.',
    ctaText: CAT.en.testCta,
    crossSellText: 'Want to qualify as a Digital Broker? See Premium',
    crossSellLink: '/membresias/premium',
    forYou: [
      'You want access to property opportunities that never reach the portals, and to choose for yourself which ones you take part in',
      'You want a thorough understanding of how the property sector is changing',
      'You’re looking for a serious community of members and professionals',
      'You prefer practical training built on real cases',
      'You want to learn at your own pace, with live sessions along the way',
    ],
    notForYou: [
      'You want the certification and the Bootcamp (that’s Premium)',
      'You’re only after introductory content (that’s Access)',
      'You don’t have time to train or attend sessions',
    ],
    benefits: [
      { icon: KeyRound, t: 'Members’ area of opportunities', d: 'Real off-market property opportunities, presented with full documentation and analysis. You choose which ones you take part in.' },
      { icon: BookOpen, t: 'FHC Academy · advanced training', d: 'A complete programme on the new property economy and tokenisation as a discipline, from first principles to practice.' },
      { icon: Video, t: 'Live specialist sessions', d: 'Masterclasses and Q&A sessions with the team and guest speakers.' },
      { icon: Users, t: 'Private community', d: 'A closed circle of members and professionals to share perspectives with.' },
      { icon: Globe2, t: 'Networking with members and professionals', d: 'Connections with members and professionals from different countries.' },
      { icon: FileText, t: 'Extended resources', d: 'Up-to-date guides, templates and study materials.' },
    ],
    timeline: [
      { label: 'Day 1', text: 'Access to the training platform and the private community' },
      { label: 'Week 1', text: 'Welcome session and your first FHC Academy modules' },
      { label: 'Month 1', text: 'Your first live specialist sessions and connections with other members' },
      { label: 'Month 3', text: 'The judgement to assess the club’s opportunities and decide which ones to take part in' },
    ],
    faq: [
      ...SHARED_FAQ_EN.slice(0, 3),
      { q: 'Can I move up to Premium later on?', a: 'Yes. Get in touch and we’ll explain the current terms for moving up to Premium.' },
      ...SHARED_FAQ_EN.slice(3),
    ],
  },
  premium: {
    badge: 'PREMIUM MEMBERSHIP — FREEDOM HOMES CLUB',
    title: 'Get certified as a Digital Broker in Property Tokenisation',
    subtitle: 'Everything in PRO, plus the in-person Digital Broker Bootcamp, the private FHC certification and weekly live group mentoring.',
    ctaText: CAT.en.testCta,
    crossSellText: 'Rather start with the training? See PRO',
    crossSellLink: '/membresias/pro',
    forYou: [
      'You want a serious club certification in property tokenisation',
      'You value a 4-day in-person immersion with the team',
      'You want weekly live group mentoring',
      'You want to be able to explain property tokenisation rigorously in your professional circle',
    ],
    notForYou: [
      'You’re only after training and community (that’s PRO)',
      'You can’t attend an in-person Bootcamp',
      'You’re looking for an official academic qualification',
    ],
    benefitsIntro: 'EVERYTHING IN PRO',
    benefits: [
      { icon: CalendarDays, t: 'Digital Broker Bootcamp · 4 days in person', d: `An in-person immersion with the FHC team. ${CAT.en.bootcampNote}` },
      { icon: Award, t: 'Private FHC certification as a Digital Broker', d: CAT.en.certNote },
      { icon: GraduationCap, t: 'Digital Broker credential', d: 'A private certification that recognises your training on the programme, backed by the team’s standards and materials.' },
      { icon: Sparkles, t: 'Weekly group mentoring', d: 'Weekly live sessions to work through cases and keep up to date.' },
      { icon: FileText, t: 'Professional club materials', d: 'Presentations and guides to explain property tokenisation rigorously.' },
    ],
    timeline: [
      { label: 'Day 1', text: 'Full access to the platform, the community and FHC Academy' },
      { label: 'Week 1', text: 'Welcome session and booking your place on the next Bootcamp' },
      { label: 'Bootcamp', text: '4 days of in-person immersion and the private FHC certification' },
      { label: 'Afterwards', text: 'Weekly group mentoring and the club’s professional materials' },
    ],
    faq: [
      ...SHARED_FAQ_EN.slice(0, 1),
      { q: 'What does the Bootcamp include?', a: `Four days of in-person training with the team, ending with the private FHC certification. ${CAT.en.bootcampNote} Upcoming dates are listed in the events section.` },
      { q: 'Is the certification official?', a: CAT.en.certNote },
      ...SHARED_FAQ_EN.slice(1),
    ],
  },
};

const T = {
  es: {
    configs: CONFIGS_ES,
    includesCert: 'INCLUYE CERTIFICACIÓN',
    heroMeta: 'Membresía anual · Comunidad privada',
    forYouTitle: 'Esta membresía es para ti si…',
    notForYouTitle: 'Probablemente no es para ti si…',
    includesKicker: 'Qué incluye',
    includesTitle: (name) => `Tu membresía ${name}, al detalle`,
    benefitsIntroRest: '— área privada de oportunidades, formación avanzada, sesiones especializadas y comunidad',
    pathKicker: 'Tu camino',
    pathTitle: 'Qué sucede después de unirte',
    satisfactionTitle: 'Decide con margen',
    termsLink: 'Leer los Términos y Condiciones',
    finalTitle: '¿Listo para entrar en El Círculo?',
    finalText: (name) => `Empieza por el test de idoneidad. Después eliges día y hora para hablar con el equipo: te explicamos la membresía ${name}, su cuota y si es el nivel que encaja contigo.`,
    faqTitle: 'Preguntas frecuentes',
  },
  en: {
    configs: CONFIGS_EN,
    includesCert: 'INCLUDES CERTIFICATION',
    heroMeta: 'Annual membership · Private community',
    forYouTitle: 'This membership is for you if…',
    notForYouTitle: 'It’s probably not for you if…',
    includesKicker: 'What’s included',
    includesTitle: (name) => `Your ${name} membership, in detail`,
    benefitsIntroRest: '— members’ area of opportunities, advanced training, specialist sessions and community',
    pathKicker: 'Your path',
    pathTitle: 'What happens after you join',
    satisfactionTitle: 'Decide with peace of mind',
    termsLink: 'Read the Terms and Conditions',
    finalTitle: 'Ready to step inside the Circle?',
    finalText: (name) => `Start with the suitability test. Then choose a day and time to speak with the team: we’ll walk you through the ${name} membership, its fee and whether it’s the right tier for you.`,
    faqTitle: 'Frequently asked questions',
  },
};

export default function MembresiaDetalle({ type }) {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const cat = publicCatalog(lang);
  const c = t.configs[type] || t.configs.basica;
  const plan = cat.plans[type] || cat.plans.basica;
  const recommended = type === 'premium';
  const [openFaq, setOpenFaq] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);


  return (
    <MembresiasLayout>
      {/* ═══ 1. HERO ═══ */}
      <section style={{ background: NAV, padding: '80px 16px 72px' }} data-testid="detalle-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] mb-5" style={{ color: GOLD, border: `1px solid ${GOLD}40`, borderRadius: 4 }}>
            {c.badge}
          </p>
          {recommended && (
            <div className="mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full" style={{ background: `${GOLD}20`, color: GOLD }}>
                <Star className="w-3.5 h-3.5 fill-current" /> {t.includesCert}
              </span>
            </div>
          )}
          <h1 className="text-3xl md:text-5xl leading-tight mb-5 text-white" style={{ fontFamily: SERIF }}>{c.title}</h1>
          <p className="text-base md:text-lg mb-8" style={{ color: 'rgba(255,255,255,0.55)', maxWidth: 620, margin: '0 auto 32px' }}>{c.subtitle}</p>
          <GoldCTA onClick={openModal} testId="detalle-hero-cta">{c.ctaText} <ArrowRight className="w-4 h-4" /></GoldCTA>
          <p className="text-xs mt-4 max-w-md mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{cat.nextStepNote}</p>
          <p className="flex items-center justify-center gap-2 text-sm mt-5" style={{ color: 'rgba(255,255,255,0.45)' }}>
            <Users className="w-4 h-4" style={{ color: GOLD }} /> {t.heroMeta}
          </p>
        </div>
      </section>

      {/* ═══ 2. PARA QUIÉN ES ═══ */}
      <section style={{ background: CREAM, padding: '64px 16px' }} data-testid="detalle-para-quien">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="p-6 bg-white border" style={{ borderColor: '#ddd', borderRadius: 4 }}>
            <h3 className="flex items-center gap-2 text-lg font-bold mb-5" style={{ color: NAV }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: '#22c55e' }} /> {t.forYouTitle}
            </h3>
            <div className="space-y-3">
              {c.forYou.map((item) => (
                <p key={item} className="flex items-start gap-2.5 text-sm" style={{ color: '#555' }}>
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} /> {item}
                </p>
              ))}
            </div>
          </div>
          <div className="p-6 bg-white border" style={{ borderColor: '#ddd', borderRadius: 4 }}>
            <h3 className="flex items-center gap-2 text-lg font-bold mb-5" style={{ color: NAV }}>
              <X className="w-5 h-5" style={{ color: '#ef4444' }} /> {t.notForYouTitle}
            </h3>
            <div className="space-y-3">
              {c.notForYou.map((item) => (
                <p key={item} className="flex items-start gap-2.5 text-sm" style={{ color: '#888' }}>
                  <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#ccc' }} /> {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 3. QUÉ INCLUYE ═══ */}
      <section style={{ background: '#fff', padding: '64px 16px' }} data-testid="detalle-beneficios">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: GOLD }}>{t.includesKicker}</p>
          <h2 className="text-2xl md:text-3xl text-center mb-10" style={{ color: NAV, fontFamily: SERIF }}>{t.includesTitle(plan.name)}</h2>
          {c.benefitsIntro && (
            <div className="p-4 mb-8 flex items-center gap-3 border" style={{ borderColor: GOLD, borderRadius: 4, background: `${GOLD}08` }}>
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: GOLD }} />
              <p className="text-sm font-bold" style={{ color: NAV }}>{c.benefitsIntro} <span className="font-normal" style={{ color: '#888' }}>{t.benefitsIntroRest}</span></p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {c.benefits.map((b) => (
              <div key={b.t} className="p-5 border flex gap-4" style={{ borderColor: '#eee', borderRadius: 4 }}>
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: NAV, borderRadius: 4 }}>
                  <b.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold mb-1" style={{ color: NAV }}>{b.t}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: '#888' }}>{b.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 4. TU CAMINO ═══ */}
      <section style={{ background: CREAM, padding: '64px 16px' }} data-testid="detalle-timeline">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: GOLD }}>{t.pathKicker}</p>
          <h2 className="text-2xl md:text-3xl text-center mb-12" style={{ color: NAV, fontFamily: SERIF }}>{t.pathTitle}</h2>
          <div className="relative pl-8 md:pl-12">
            <div className="absolute left-3 md:left-5 top-0 bottom-0 w-px" style={{ background: GOLD }} />
            {c.timeline.map((step) => (
              <div key={step.label} className="relative mb-10 last:mb-0">
                <div className="absolute -left-5 md:-left-7 w-4 h-4 rounded-full border-2 bg-white" style={{ borderColor: GOLD, top: 4 }} />
                <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: GOLD }}>{step.label}</p>
                <p className="text-sm leading-relaxed" style={{ color: '#555' }}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. PERÍODO DE SATISFACCIÓN ═══ */}
      <section style={{ background: '#fff', padding: '64px 16px' }} data-testid="detalle-garantia">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full" style={{ background: `${GOLD}15` }}>
            <Shield className="w-8 h-8" style={{ color: GOLD }} />
          </div>
          <h2 className="text-2xl mb-3" style={{ color: NAV, fontFamily: SERIF }}>{t.satisfactionTitle}</h2>
          <p className="text-sm leading-relaxed mb-5" style={{ color: '#666' }}>{cat.satisfactionNote}</p>
          <Link to="/terms-conditions" className="text-xs font-bold underline" style={{ color: GOLD }}>{t.termsLink}</Link>
        </div>
      </section>

      {/* ═══ 6. CTA FINAL ═══ */}
      <section style={{ background: NAV, padding: '72px 16px' }} data-testid="detalle-cta-final">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl mb-4 text-white" style={{ fontFamily: SERIF }}>{t.finalTitle}</h2>
          <p className="text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t.finalText(plan.name)}
          </p>
          <div className="mb-8">
            <GoldCTA full onClick={openModal} testId="detalle-cta-final-btn">{c.ctaText} <ArrowRight className="w-4 h-4" /></GoldCTA>
          </div>
          <Link to={c.crossSellLink} className="text-sm transition-colors hover:underline" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {c.crossSellText} <ArrowRight className="w-3 h-3 inline" />
          </Link>
        </div>
      </section>

      {/* ═══ 7. FAQ ═══ */}
      <section style={{ background: CREAM, padding: '64px 16px' }} data-testid="detalle-faq">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: GOLD }}>FAQ</p>
          <h2 className="text-2xl md:text-3xl text-center mb-10" style={{ color: NAV, fontFamily: SERIF }}>{t.faqTitle}</h2>
          {c.faq.map((f, i) => (
            <div key={f.q} style={{ borderBottom: '1px solid #ddd' }}>
              <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 py-5 text-left group" data-testid={`detalle-faq-${i}`} aria-expanded={openFaq === i}>
                <span className="flex items-center gap-3 pr-4 text-base font-semibold group-hover:opacity-80 transition-opacity" style={{ color: NAV }}>
                  <HelpCircle className="w-5 h-5 flex-shrink-0" style={{ color: GOLD }} /> {f.q}
                </span>
                <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color: '#bbb', transform: openFaq === i ? 'rotate(180deg)' : 'none' }} />
              </button>
              {openFaq === i && <p className="pl-8 pb-5 text-sm leading-relaxed" style={{ color: '#666' }}>{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      <QualificationFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source={`membresias-${plan.name.toLowerCase()}`} lang={lang} />
    </MembresiasLayout>
  );
}
